import React, { useCallback, useRef } from 'react';
import mergeRefs from 'react-merge-refs';
import { conformToMask, type IMaskConfig } from 'text-mask-core';

import { MaskedInput, type CommonMaskedInputProps } from '../MaskedInput';
import { deleteFormatting, deleteMaskChar, getInsertedNumber, setCaretPosition, checkInsertBefore } from './utils';

const mask = ['+', '7', ' ', /([0-6]|[8-9])/, /\d/, /\d/, ' ', /\d/, /\d/, /\d/, '-', /\d/, /\d/, '-', /\d/, /\d/];

const countryPrefix = '+7 ';

type CommonPhoneInputProps = Omit<CommonMaskedInputProps, 'onBeforeDisplay' | 'type' | 'mask'> & {
  clearableCountryCode?: boolean;
};

export const PhoneInput = React.forwardRef<HTMLInputElement, CommonPhoneInputProps>(
  ({ clearableCountryCode = true, ...restProps }, ref) => {
    const inputRef = useRef<HTMLInputElement>(null);

    const handleBeforeDisplay = useCallback(
      (conformedValue: string, config: IMaskConfig) => {
        const { rawValue, previousConformedValue, currentCaretPosition } = config;

        const previousValueWithoutFormatting = previousConformedValue ? deleteFormatting(previousConformedValue) : '';

        const digits = rawValue.replace(/\D/g, '');

        if ((digits.length === 10 || digits.length === 11) && !rawValue.startsWith('+7')) {
          let processedDigits = digits;

          if (digits.length === 11 && (digits.startsWith('7') || digits.startsWith('8'))) {
            processedDigits = digits.slice(1);
          }
          const masked = conformToMask(`+7${processedDigits}`, mask, config);

          return masked.conformedValue;
        }

        const nextConformedValue = checkInsertBefore(previousConformedValue, rawValue)
          ? conformToMask(deleteMaskChar(previousValueWithoutFormatting, rawValue), mask, config).conformedValue
          : conformedValue;

        const currentValueWithoutFormatting = deleteFormatting(nextConformedValue) || '';

        if (
          previousConformedValue &&
          (([3, 6].includes(currentCaretPosition) &&
            Math.abs(previousValueWithoutFormatting.length - currentValueWithoutFormatting.length) === 1) ||
            ([7, 10, 13].includes(currentCaretPosition) && previousConformedValue.length > currentCaretPosition))
        ) {
          setCaretPosition({ position: currentCaretPosition, inputRef });
        }

        if (rawValue === '+7' && nextConformedValue === '' && clearableCountryCode) {
          setCaretPosition({ position: countryPrefix.length - 1, inputRef });
        }

        if (rawValue === countryPrefix) {
          return rawValue;
        }

        const insertedNumber = getInsertedNumber({
          rawValue,
          clearableCountryCode,
          countryPrefix,
          previousConformedValue,
        });

        if ((insertedNumber.startsWith('7') || insertedNumber.startsWith('8')) && insertedNumber.length >= 10) {
          const masked = conformToMask(`+7${insertedNumber.slice(1)}`, mask, config);

          return masked.conformedValue;
        }

        if (rawValue.length === 1 && ['7', '8'].includes(rawValue[0])) {
          return countryPrefix;
        }

        if (!clearableCountryCode && !nextConformedValue) {
          setCaretPosition({ position: countryPrefix.length, inputRef });

          return rawValue.length ? false : countryPrefix;
        }

        return nextConformedValue;
      },
      [clearableCountryCode],
    );

    return (
      <MaskedInput
        {...restProps}
        type="tel"
        ref={mergeRefs([ref, inputRef])}
        defaultValue={clearableCountryCode ? restProps.defaultValue : countryPrefix}
        mask={mask}
        onBeforeDisplay={handleBeforeDisplay}
      />
    );
  },
);

PhoneInput.displayName = 'PhoneInputs';
